import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { NavigationItem, SiteSettings } from "@/types/content";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);

  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);

  const fallbackNavItems: NavigationItem[] = [
    { label: "home", sectionId: "home", isEnabled: true, sortOrder: 1 },
    { label: "about", sectionId: "about", isEnabled: true, sortOrder: 2 },
    { label: "projects", sectionId: "projects", isEnabled: true, sortOrder: 3 },
    { label: "skills", sectionId: "skills", isEnabled: true, sortOrder: 4 },
    { label: "blog", sectionId: "blog", isEnabled: true, sortOrder: 5 },
    { label: "contact", sectionId: "contact", isEnabled: true, sortOrder: 6 },
  ];

  const navigationItems = useMemo(() => {
    const items = settings?.navigationItems?.length ? settings.navigationItems : fallbackNavItems;
    const filteredItems = items
      .filter((item) => item.sectionId && item.isEnabled !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    if (settings?.blogSectionEnabled === false) {
      return filteredItems.filter((item) => item.sectionId !== "blog");
    }
    return filteredItems;
  }, [settings?.navigationItems, settings?.blogSectionEnabled]);

  const brandLabel = settings?.navigationBrand || "callie.exe";

  const normalizeSectionId = (sectionId: string) => sectionId.replace(/^#/, "").replace(/^\.?\//, "");

  const scrollToSection = (rawSectionId: string) => {
    const sectionId = normalizeSectionId(rawSectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 72;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementTop - headerOffset, behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 w-full z-50 nav-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <span className="text-xl font-mono font-bold text-matrix-green">&gt; {brandLabel}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navigationItems.map((item) => (
                <button
                  key={`${item.sectionId}-${item.label}`}
                  onClick={() => scrollToSection(item.sectionId)}
                  type="button"
                  className="text-gray-300 hover:text-matrix-green transition-colors duration-300 px-3 py-2 font-mono"
                >
                  ./{item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:text-matrix-green"
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden" id="mobile-navigation">
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="relative z-50 px-2 pt-2 pb-3 space-y-1 glass-card mt-2 rounded-lg">
              {navigationItems.map((item) => (
                <button
                  key={`${item.sectionId}-${item.label}-mobile`}
                  onClick={() => scrollToSection(item.sectionId)}
                  type="button"
                  className="text-gray-300 hover:text-matrix-green block px-3 py-2 font-mono w-full text-left"
                >
                  ./{item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
